import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface ApiErrorMeta {
  status: number;
  code?: string;
}

/**
 * 底层 HTTP 抽象。
 *
 * 存在的意义是让「并发 401 只刷新一次」这条不变量**可以在无网络下单测**
 * （见 `__tests__/concurrent-401.spec.ts`）：生产用 Axios 适配器，
 * 测试用内存适配器，业务代码不需要知道区别。
 */
export interface HttpAdapter {
  request<T>(config: AxiosRequestConfig): Promise<T>;
}

export interface ApiClientOptions {
  baseURL: string;
  /** 读取当前令牌对 */
  getTokens: () => TokenPair | null;
  /** 刷新成功后写回新令牌对 */
  onTokens: (tokens: TokenPair) => void;
  /** 刷新失败（refresh token 过期）时调用 */
  onUnauthorized: () => void;
  /** 刷新端点路径，默认 `/auth/refresh` */
  refreshPath?: string;
  /**
   * 仅用于测试：替换整个客户端的底层 HTTP 实现。
   *
   * **它会同时接管业务请求通道与刷新通道**（不带 `Authorization: Bearer`，
   * 只有 `{ refreshToken }` 请求体）。两个通道分开建模是刻意的：刷新用刷新令牌，
   * 带上过期的 access token 既无意义也会让测试适配器难以表达。
   */
  adapter?: HttpAdapter;
}

export interface ApiClient {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  del<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  /** 暴露给领域模块使用（已带鉴权头） */
  raw: HttpAdapter;
  /** 刷新端点被调用的次数（测试断言用） */
  refreshCallCount: () => number;
}

/** 从任意异常中提取 HTTP 状态码与业务错误码 */
export function readErrorMeta(error: unknown): ApiErrorMeta | null {
  if (!axios.isAxiosError(error)) return null;
  const status = error.response?.status;
  if (typeof status !== 'number') return null;
  const body = error.response?.data as { code?: string } | undefined;
  return { status, ...(body?.code === undefined ? {} : { code: body.code }) };
}

function toAdapter(instance: AxiosInstance): HttpAdapter {
  return {
    async request<T>(config: AxiosRequestConfig): Promise<T> {
      const res = await instance.request<T>(config);
      return res.data;
    },
  };
}

/**
 * 创建带 401 单飞刷新的 API 客户端。
 *
 * 关键不变量（`REQ-AUTH-003` / `UC-AUTH-003`）：
 *   N 个并发请求同时收到 401 时，只允许发起 **1** 次 `/auth/refresh`；
 *   其余请求等待同一个 Promise，刷新成功后各自重放。
 *
 * 算法来源：基线 `cuito-cashier-web/src/api/client.ts:16-45` 的 `refreshPromise` 去重。
 * 四条实现要点（改动会导致去重失效、丢鉴权头或递归）：
 *   ① **鉴权头在适配器边界注入**（而不是 axios 请求拦截器）—— 自定义 adapter 会绕过
 *      实例的拦截器链，若把注入放在拦截器里，注入 adapter 时请求就不带鉴权头；
 *   ② `refreshPromise` 必须在 `async` IIFE **之外**同步赋值，否则并发调用看不到它；
 *      清空放在微任务末尾，让等待者先共享到同一个 Promise；
 *   ③ 重放只允许一次：刷新成功但业务仍 401 时直接抛出，不继续刷新；
 *   ④ 刷新调用**不在** 401 的 catch 块内，否则刷新端点返回 401 会再触发刷新 → 递归。
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const {
    baseURL,
    getTokens,
    onTokens,
    onUnauthorized,
    refreshPath = '/auth/refresh',
    adapter,
  } = options;

  // 业务请求通道：每次请求前现读令牌并注入鉴权头。
  //
  // 这里**不用 axios 的请求拦截器**：自定义 adapter（测试注入）会绕过实例的
  // 拦截器链，导致请求不带 Authorization 头——鉴权头必须在适配器边界注入，
  // 才能让「生产 Axios」与「测试内存适配器」走同一条逻辑。
  const authedInstance = axios.create({ baseURL, timeout: 15000 });
  const transport: HttpAdapter = adapter ?? toAdapter(authedInstance);
  const authed: HttpAdapter = {
    async request<T>(config: AxiosRequestConfig): Promise<T> {
      const tokens = getTokens();
      const headers: AxiosRequestConfig['headers'] = { ...(config.headers ?? {}) };
      if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return transport.request<T>({ ...config, headers });
    },
  };

  // 刷新通道：不带鉴权头（刷新令牌在请求体里）。
  // 注入 adapter 时同样接管此通道，否则测试无法观察到刷新调用。
  const bareInstance = axios.create({ baseURL, timeout: 15000 });
  const refreshHttp: HttpAdapter = adapter ?? toAdapter(bareInstance);

  let refreshPromise: Promise<TokenPair | null> | null = null;
  let refreshCalls = 0;

  /** 执行刷新；同一时刻只允许一个在飞 */
  function refreshOnce(): Promise<TokenPair | null> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async (): Promise<TokenPair | null> => {
      const tokens = getTokens();
      if (!tokens?.refreshToken) return null;
      refreshCalls += 1;
      try {
        const next = await refreshHttp.request<TokenPair>({
          method: 'post',
          url: refreshPath,
          data: { refreshToken: tokens.refreshToken },
        });
        onTokens(next);
        return next;
      } catch {
        return null;
      } finally {
        queueMicrotask(() => {
          refreshPromise = null;
        });
      }
    })();

    return refreshPromise;
  }

  async function send<T>(
    method: 'get' | 'post' | 'patch' | 'put' | 'delete',
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const doRequest = (): Promise<T> =>
      authed.request<T>({
        ...config,
        method,
        url,
        ...(data === undefined ? {} : { data }),
      });

    let firstFailure: unknown;
    try {
      return await doRequest();
    } catch (error) {
      const meta = readErrorMeta(error);
      if (meta?.status !== 401) throw error;
      firstFailure = error;
    }

    const refreshed = await refreshOnce();
    if (!refreshed) {
      onUnauthorized();
      throw firstFailure;
    }

    return doRequest();
  }

  return {
    get: (url, config) => send('get', url, undefined, config),
    post: (url, data, config) => send('post', url, data, config),
    patch: (url, data, config) => send('patch', url, data, config),
    put: (url, data, config) => send('put', url, data, config),
    del: (url, config) => send('delete', url, undefined, config),
    raw: authed,
    refreshCallCount: () => refreshCalls,
  };
}
